// src/pages/prodactions/production-summary/SummaryCards.js
import React from "react";
import { Box, Grid, Stack, Card, CardContent, Typography, Divider, Chip, alpha } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PublicIcon from "@mui/icons-material/Public";
import CategoryIcon from "@mui/icons-material/Category";

// Fungsi untuk format ribuan dengan desimal
const formatNumber = (value) => {
    if (!value && value !== 0) return "0";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "0";
    return numValue.toLocaleString("id-ID");
};

// Komponen Card untuk menampilkan total
// 
export const TotalCard = ({ title, totalRencana, totalAktual, icon: Icon, color = "#2196f3", backgroundColor, borderHoverColor }) => {
    const theme = useTheme();
    const persentase = totalRencana > 0 ? ((totalAktual / totalRencana) * 100).toFixed(1) : 0;
    const selisih = totalAktual - totalRencana;
    const status = selisih >= 0 ? "positive" : "negative";

    return (
        <Card
            sx={{
                height: "100%",
                boxSizing: "border-box",
                transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
                backgroundColor: backgroundColor,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: "none", // Hilangkan boxShadow default
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: theme.shadows[4], // BoxShadow hanya muncul saat hover
                    borderColor: borderHoverColor,
                },
            }}
        >
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center">
                        <Box
                            sx={{
                                backgroundColor: color,
                                borderRadius: 2,
                                p: 1,
                                mr: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Icon sx={{ color: "#ffffff", fontSize: 20 }} />
                        </Box>
                        <Typography variant="body2" fontWeight="medium">
                            {title}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={`${persentase}%`}
                        color={status === "positive" ? "success" : "warning"}
                        variant="outlined"
                        sx={{ fontWeight: "bold" }}
                    />
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Rencana
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                            {formatNumber(totalRencana)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Aktual
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                            {formatNumber(totalAktual)}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                        Selisih
                    </Typography>
                    <Box display="flex" alignItems="center">
                        {status === "positive" ? (
                            <TrendingUpIcon sx={{ color: "#2e7d32", fontSize: 16, mr: 0.5 }} />
                        ) : (
                            <TrendingDownIcon sx={{ color: "#d32f2f", fontSize: 16, mr: 0.5 }} />
                        )}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: "bold",
                                color: status === "positive" ? "#2e7d32" : "#d32f2f",
                            }}
                        >
                            {formatNumber(Math.abs(selisih))}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// Komponen untuk menampilkan total kategori (BIG, MEDIUM, SMALL) dengan selisih
export const CategoryCard = ({ title, totalRencana, totalAktual, icon: Icon, color = "#ff9800", borderHoverColor }) => {
    const theme = useTheme();
    const persentase = totalRencana > 0 ? ((totalAktual / totalRencana) * 100).toFixed(1) : 0;
    const selisih = totalAktual - totalRencana;
    const status = selisih >= 0 ? "positive" : "negative";

    return (
        <Card
            sx={{
                justifyContent:'space-between',
                alignItems:'stretch',
                height:'100%',
                width: "100%",
                border: `1px solid ${theme.palette.divider}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                boxShadow: "none", // Hilangkan boxShadow default
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: theme.shadows[4], // BoxShadow hanya muncul saat hover
                    borderColor: borderHoverColor,
                },
            }}
        >
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center">
                        <Box
                            sx={{
                                backgroundColor: alpha(color, 0.1),
                                borderRadius: 2,
                                p: 1,
                                mr: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Icon sx={{ color: color, fontSize: 16 }} />
                        </Box>
                        <Typography variant="body2" fontWeight="medium">
                            {title}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={`${persentase}%`}
                        color={parseFloat(persentase) >= 100 ? "success" : "warning"}
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.625rem" }}
                    />
                </Box>

                <Box display="flex" justifyContent="space-between" mt={1} mb={1}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Rencana
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                            {formatNumber(totalRencana)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Aktual
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                            {formatNumber(totalAktual)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Selisih
                        </Typography>
                        {status === "positive" ? (
                            <TrendingUpIcon sx={{ color: "#2e7d32", fontSize: 14, mr: 0.5 }} />
                        ) : (
                            <TrendingDownIcon sx={{ color: "#d32f2f", fontSize: 14, mr: 0.5 }} />
                        )}
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: "bold",
                                color: status === "positive" ? "#2e7d32" : "#d32f2f",
                            }}
                        >
                            {formatNumber(Math.abs(selisih))}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// Komponen utama untuk menampilkan semua summary cards
export const SummaryCards = ({ totals }) => {
    return (
        <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid size={12}>
                <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2} alignItems="stretch">
                        <Grid size={4} sx={{ display: 'flex', height:'372px' }}>
                            <Stack spacing={2} sx={{ flex: 1 }}>
                                <TotalCard
                                    title="EXPORT"
                                    sx={{ flex: 1 }}
                                    totalRencana={totals.export.rencana}
                                    totalAktual={totals.export.aktual}
                                    icon={PublicIcon}
                                    color="#2196f3"
                                    backgroundColor="#e8f4fe"
                                    borderHoverColor="#2196f3"
                                />
                                <TotalCard
                                    title="LOCAL"
                                    sx={{
                                        flex: 1
                                    }}
                                    totalRencana={totals.local.rencana}
                                    totalAktual={totals.local.aktual}
                                    icon={PublicIcon}
                                    color="#4caf50"
                                    backgroundColor="#edf7ed"
                                    borderHoverColor="#4caf50"
                                />
                            </Stack>
                        </Grid>
                        <Grid size={4} sx={{ display: 'flex', height:'372px' }}>
                            <Stack spacing={2} sx={{ flex: 1 }}>
                                <TotalCard
                                    title="MIDDLE EAST"
                                    sx={{ flex: 1 }}
                                    totalRencana={totals.middleEast.rencana}
                                    totalAktual={totals.middleEast.aktual}
                                    icon={PublicIcon}
                                    color="#ff9800"
                                    backgroundColor="#fff4e5"
                                    borderHoverColor="#ff9800"
                                />
                                <TotalCard
                                    title="ASIA"
                                    sx={{ flex: 1 }}
                                    totalRencana={totals.asia.rencana}
                                    totalAktual={totals.asia.aktual}
                                    icon={PublicIcon}
                                    color="#f44336"
                                    backgroundColor="#feeceb"
                                    borderHoverColor="#f44336"
                                />
                            </Stack>
                        </Grid>
                        <Grid size={4} sx={{ display: 'flex', height:'372px' }}>
                            <Stack spacing={2} sx={{ flex: 1, justifyContent:'space-between' }}>
                                <CategoryCard
                                    title="BIG"
                                    totalRencana={totals.big.rencana}
                                    totalAktual={totals.big.aktual}
                                    icon={CategoryIcon}
                                    color="#9c27b0" // Diubah menjadi ungu
                                    borderHoverColor="#9c27b0"
                                />
                                <CategoryCard
                                    title="MEDIUM"
                                    totalRencana={totals.medium.rencana}
                                    totalAktual={totals.medium.aktual}
                                    icon={CategoryIcon}
                                    color="#795548" // Diubah menjadi coklat
                                    borderHoverColor="#795548"
                                />
                                <CategoryCard
                                    title="SMALL"
                                    totalRencana={totals.small.rencana}
                                    totalAktual={totals.small.aktual}
                                    icon={CategoryIcon}
                                    color="#607d8b" // Diubah menjadi biru abu-abu
                                    borderHoverColor="#607d8b"
                                />
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    );
};

export default SummaryCards;